/* Exporting and closing an account — STORY-010.
 *
 * The export shape is a published contract in a way most responses are not: the
 * story requires a format "a consumer can be written against", and somebody
 * exercising a legal right may well parse it with a script. So it is flat,
 * boring, and mirrors what is held rather than what a screen wants.
 */
import { LOCALES } from '@eventuley/i18n';
import * as z from 'zod';
export const accountExportSchema = z.object({
    /* Stamped by the server, so a downloaded file says when it was true. */
    exportedAt: z.string(),
    user: z.object({
        id: z.string(),
        email: z.string(),
        displayName: z.string(),
        locale: z.string(),
        createdAt: z.string(),
        emailVerifiedAt: z.string().nullable(),
    }),
    agreements: z.array(z.object({ document: z.string(), version: z.string(), acceptedAt: z.string() })),
    memberships: z.array(z.object({
        organisationId: z.string(),
        organisationName: z.string(),
        role: z.string(),
        joinedAt: z.string(),
    })),
    joinRequests: z.array(z.object({ organisationId: z.string(), requestedAt: z.string() })),
    /**
     * What this file does NOT contain, said in the file itself.
     *
     * A Member asking for "everything about me" reasonably expects the
     * workspace's guests and money too, and those are the customer's data rather
     * than ours — we are the processor there and the request goes to them
     * (STORY-019). Saying so here and on the screen is cheaper than answering the
     * support email, and more honest than an export that quietly omits it.
     */
    excludes: z.array(z.string()),
});
/** One workspace the caller is the last Owner of, and must answer for. */
export const soleOwnedOrganisationSchema = z.object({
    organisationId: z.string(),
    name: z.string(),
    otherMembers: z.number().int().nonnegative(),
});
export const accountStatusSchema = z.object({
    /** When the account closes, or null while it is live. */
    closesAt: z.string().nullable(),
    /**
     * How long the grace period is.
     *
     * Sent rather than assumed, because the copy says it out loud — "nothing is
     * destroyed for 30 days" — and it is an operator setting that can be any
     * value from 1 to 90. A client that hardcoded 30 would tell somebody the
     * wrong number the first time it was changed, which on this screen is the
     * number they are relying on.
     */
    graceDays: z.number().int().positive(),
    /**
     * The questions outstanding before closure can be requested.
     *
     * Derived on every read and never stored — promote somebody on the Members
     * screen and the question disappears on the next render, which is the
     * behaviour the prototype settled.
     */
    soleOwned: z.array(soleOwnedOrganisationSchema),
});
export const closeAccountRequestSchema = z.object({
    /* Re-authentication. A hijacked session must not be able to destroy an
     * account, and this is the one field that makes that true — `security.md`
     * A07 wants a privilege change to cost the credential again. */
    password: z.string().min(1),
    /**
     * The Organisations to close along with the account.
     *
     * Every workspace the caller is the last Owner of must appear here, and the
     * server refuses if one is missing. "I will hand it over first" is a statement
     * of intent rather than an answer — it is expressed by promoting somebody,
     * after which that workspace is no longer sole-owned and no longer needs to be
     * named.
     */
    closeOrganisationIds: z.array(z.string()),
});
export const closeAccountResponseSchema = z.object({
    closesAt: z.string(),
    /** Named back, so the confirmation can say what goes with it. */
    closingOrganisations: z.array(z.string()),
});
/**
 * Changing the language you read the app in — STORY-042.
 *
 * A User setting, not an Organisation one: a person in three workspaces has one
 * language. It is not an Owner action and it is not tenant-scoped, which is why
 * it sits on the account rather than under `/organisations/{id}`.
 *
 * The value is checked against the locales the product actually ships, not
 * against a shape — an unsupported tag stored here would strand somebody on a
 * screen they cannot read, and `i18n.md`'s fallback chain is for reading, not
 * for writing.
 */
export const updateLocaleRequestSchema = z.object({
    locale: z.enum(LOCALES),
});
export const localeResponseSchema = z.object({ locale: z.enum(LOCALES) });
//# sourceMappingURL=accounts.js.map